import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, VolumeX } from 'lucide-react';
import { REVIEW_CAPTION_SETS, ReviewCaptionSet } from '../config/reviewCaptions';
import { REVIEW_EVENT_NAME, ReviewEvent } from '../utils/reviewEvents';

// Modo de gravação do vídeo de App Review da Meta. Liga com ?reviewMode=1
// (fluxo Automático) ou ?reviewMode=manual (plano B) na URL e desliga com
// ?reviewMode=0. Fica ativo na aba até ser desligado, mesmo navegando pelo
// sistema. Mostra a legenda em inglês do passo atual no canto inferior esquerdo
// (o popup da Meta abre à direita, sem cobri-la) e a narra com a voz em inglês
// do navegador — o OBS grava esse áudio junto com a tela.
// Teclas: F9 narra a legenda atual (na primeira vez) ou avança e narra a
// próxima; F8 volta; Shift+F9 esconde/mostra; Shift+F8 liga/desliga a voz.
// Nos passos dentro da janela da Meta (onde o F9 não chega), a legenda avança
// sozinha pelos eventos do fluxo (trigger) ou por tempo (autoNextMs).
const ACTIVE_KEY = 'sinticket:reviewMode';
const STEP_KEY = 'sinticket:reviewModeStep';
const SPOKEN_KEY = 'sinticket:reviewModeSpoken';
const WORDS_PER_SECOND = 2.6;

const readSession = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key: string, value: string | null) => {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    // sessionStorage indisponível: o modo funciona só até recarregar a página.
  }
};

const detectCaptionSet = (): ReviewCaptionSet | null => {
  const param = new URLSearchParams(window.location.search).get('reviewMode');
  if (param === '0') {
    [ACTIVE_KEY, STEP_KEY, SPOKEN_KEY].forEach((key) => writeSession(key, null));
    return null;
  }
  if (param === '1' || param === 'auto' || param === 'manual') {
    const set: ReviewCaptionSet = param === 'manual' ? 'manual' : 'auto';
    if (readSession(ACTIVE_KEY) !== set) {
      writeSession(STEP_KEY, null);
      writeSession(SPOKEN_KEY, null);
    }
    writeSession(ACTIVE_KEY, set);
    return set;
  }
  const saved = readSession(ACTIVE_KEY);
  return saved === 'auto' || saved === 'manual' ? saved : null;
};

// Texto falado: "business_management" vira "business management" e a seta vira "then".
const toSpeech = (text: string) => text.replace(/_/g, ' ').replace(/→/g, ', then');

// Voz em inglês: prefere as vozes naturais (Google/Microsoft online), senão qualquer en-US.
const pickEnglishVoice = (): SpeechSynthesisVoice | null => {
  const voices = window.speechSynthesis?.getVoices() || [];
  const english = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
  return (
    english.find((v) => /Google US English/i.test(v.name)) ||
    english.find((v) => /(Aria|Jenny|Guy).*Online|Natural/i.test(v.name)) ||
    english.find((v) => v.lang === 'en-US') ||
    english[0] ||
    null
  );
};

const ReviewModeCaptions: React.FC = () => {
  const [captionSet] = useState(detectCaptionSet);
  const captions = captionSet ? REVIEW_CAPTION_SETS[captionSet] : [];
  const [hidden, setHidden] = useState(false);
  const [muted, setMuted] = useState(false);
  const [step, setStep] = useState(() => {
    const saved = Number(readSession(STEP_KEY));
    return Number.isInteger(saved) && saved >= 0 && saved < captions.length ? saved : 0;
  });

  const stepRef = useRef(step);
  const mutedRef = useRef(muted);
  const spokenRef = useRef(readSession(SPOKEN_KEY) === String(step));
  const autoNextTimer = useRef<number | null>(null);
  const keepAliveTimer = useRef<number | null>(null);
  const goAndSpeakRef = useRef<(next: number) => void>(() => {});
  mutedRef.current = muted;

  const clearAutoNext = useCallback(() => {
    if (autoNextTimer.current !== null) {
      window.clearTimeout(autoNextTimer.current);
      autoNextTimer.current = null;
    }
  }, []);

  // Narra a legenda `index`; ao terminar, agenda o avanço automático se houver.
  const speak = useCallback((index: number) => {
    const caption = captions[index];
    if (!caption) return;
    spokenRef.current = true;
    writeSession(SPOKEN_KEY, String(index));
    clearAutoNext();

    const scheduleAutoNext = () => {
      if (caption.autoNextMs === undefined || stepRef.current !== index) return;
      autoNextTimer.current = window.setTimeout(() => goAndSpeakRef.current(index + 1), caption.autoNextMs);
    };

    const synth = window.speechSynthesis;
    const voice = synth ? pickEnglishVoice() : null;
    if (mutedRef.current || !synth || !voice) {
      // Sem voz (desligada ou nenhuma voz em inglês): simula a duração da fala
      // antes do avanço automático.
      const words = caption.text.split(/\s+/).length;
      window.setTimeout(scheduleAutoNext, (words / WORDS_PER_SECOND) * 1000);
      return;
    }
    synth.cancel();
    if (keepAliveTimer.current !== null) window.clearInterval(keepAliveTimer.current);
    const utterance = new SpeechSynthesisUtterance(toSpeech(caption.text));
    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = 0.95;
    let finished = false;
    const onFinish = () => {
      if (finished) return;
      finished = true;
      if (keepAliveTimer.current !== null) window.clearInterval(keepAliveTimer.current);
      keepAliveTimer.current = null;
      scheduleAutoNext();
    };
    utterance.onend = onFinish;
    utterance.onerror = onFinish;
    synth.speak(utterance);
    // O Chrome corta falas longas (~15 s) das vozes online; pausar e retomar
    // periodicamente mantém a fala viva.
    if (!voice.localService) {
      keepAliveTimer.current = window.setInterval(() => {
        if (!synth.speaking) return;
        synth.pause();
        synth.resume();
      }, 10000);
    }
  }, [captions, clearAutoNext]);

  const goAndSpeak = useCallback((next: number) => {
    const clamped = Math.min(Math.max(next, 0), captions.length - 1);
    stepRef.current = clamped;
    setStep(clamped);
    writeSession(STEP_KEY, String(clamped));
    speak(clamped);
  }, [captions.length, speak]);
  goAndSpeakRef.current = goAndSpeak;

  // Teclado
  useEffect(() => {
    if (!captionSet) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'F9' && event.shiftKey) {
        event.preventDefault();
        setHidden((h) => !h);
      } else if (event.key === 'F8' && event.shiftKey) {
        event.preventDefault();
        setMuted((m) => {
          if (!m) window.speechSynthesis?.cancel();
          return !m;
        });
      } else if (event.key === 'F9') {
        event.preventDefault();
        if (!spokenRef.current) speak(stepRef.current);
        else goAndSpeak(stepRef.current + 1);
      } else if (event.key === 'F8') {
        event.preventDefault();
        goAndSpeak(stepRef.current - 1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [captionSet, speak, goAndSpeak]);

  // Eventos do fluxo da Meta (popup aberto, código recebido): pula para a
  // legenda correspondente, só para frente.
  useEffect(() => {
    if (!captionSet) return;
    const handleReviewEvent = (event: Event) => {
      const name = (event as CustomEvent<ReviewEvent>).detail;
      const target = captions.findIndex((c, i) => c.trigger === name && i > stepRef.current);
      if (target >= 0) goAndSpeak(target);
    };
    window.addEventListener(REVIEW_EVENT_NAME, handleReviewEvent);
    return () => window.removeEventListener(REVIEW_EVENT_NAME, handleReviewEvent);
  }, [captionSet, captions, goAndSpeak]);

  // As vozes do navegador carregam de forma assíncrona; pede a lista cedo.
  useEffect(() => {
    if (captionSet) window.speechSynthesis?.getVoices();
    return () => {
      clearAutoNext();
      if (keepAliveTimer.current !== null) window.clearInterval(keepAliveTimer.current);
      window.speechSynthesis?.cancel();
    };
  }, [captionSet, clearAutoNext]);

  if (!captionSet || hidden) return null;

  return (
    <div
      className="group fixed bottom-4 left-4 z-[9999] flex max-w-[min(60vw,760px)] items-start gap-3 rounded-lg bg-black/85 px-4 py-3 text-white shadow-2xl"
      role="status"
      aria-live="polite"
    >
      <span className="mt-0.5 flex shrink-0 items-center gap-1 rounded bg-white/15 px-1.5 py-0.5 text-xs font-semibold tabular-nums">
        {step + 1}/{captions.length}
        {muted && <VolumeX className="h-3 w-3" />}
      </span>
      <p className="text-lg leading-snug">{captions[step]?.text}</p>
      <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <button type="button" onClick={() => goAndSpeak(step - 1)} className="rounded p-0.5 hover:bg-white/20" aria-label="Previous caption">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => (spokenRef.current ? goAndSpeak(step + 1) : speak(step))} className="rounded p-0.5 hover:bg-white/20" aria-label="Next caption">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ReviewModeCaptions;
